import { SelfMonitorSample } from '../../../types/SelfMonitor';
import { SelfMonitorSeries } from './SelfMonitorChart';
import { extraNum } from './sampleUtils';

// Agent ingest volume, from the cumulative ingest_bytes_<category> counters
// the archiver writes into each of its own samples (see
// ArchiverSelfMonitor.addIngestStats). Counters are since archiver start.

const PREFIX = 'ingest_bytes_';
const TOTAL_KEY = `${PREFIX}total`;

// Known categories, in display order. Each keeps its slot - and so its
// colour - whether or not the others have data. Any category the archiver
// adds later is still shown, after these, labelled by its key.
const KNOWN_CATEGORIES: { key: string; label: string }[] = [
    { key: 'activity', label: 'Activity' },
    { key: 'statistics', label: 'Statistics' },
    { key: 'plans', label: 'SQL plans' },
    { key: 'objects', label: 'Object stats' },
    { key: 'host', label: 'Host' },
    { key: 'other', label: 'Other' },
];

const categoryKeys = (samples: SelfMonitorSample[]): string[] => {
    const known = KNOWN_CATEGORIES.map(c => c.key);
    const found = new Set<string>();
    samples.forEach(s => Object.keys(s).forEach(k => {
        if (k.startsWith(PREFIX) && k !== TOTAL_KEY) found.add(k.slice(PREFIX.length));
    }));
    return [...known, ...Array.from(found).filter(k => !known.includes(k)).sort()];
};

// Bytes per second between consecutive samples, using the real time gap so
// a missed sample does not show up as a doubled spike. A drop means the
// archiver restarted, so the counter began again from zero and the current
// value is what arrived in the gap.
export const ingestRates = (samples: SelfMonitorSample[], key: string): [number, number][] =>
    samples.slice(1).map((sample, i) => {
        const current = extraNum(sample, key);
        const previous = extraNum(samples[i], key);
        const seconds = (sample.time - samples[i].time) / 1000;
        const bytes = current >= previous ? current - previous : current;
        return [sample.time, seconds > 0 ? bytes / seconds : 0];
    });

// Average bytes per second over roughly the last minute. If the sample
// interval is a minute or longer, the window holds only the latest sample,
// so fall back to the one before it.
export const ingestRate = (samples: SelfMonitorSample[], windowMs = 60000): number => {
    if (samples.length < 2) return 0;
    const lastIndex = samples.length - 1;
    const last = samples[lastIndex];
    const firstIndex = samples.findIndex(s => s.time >= last.time - windowMs);
    const first = samples[firstIndex < lastIndex ? firstIndex : lastIndex - 1];
    const seconds = (last.time - first.time) / 1000;
    const bytes = extraNum(last, TOTAL_KEY) - extraNum(first, TOTAL_KEY);
    return seconds > 0 && bytes > 0 ? bytes / seconds : 0;
};

// Bytes received during the retained sample window (up to the last hour).
// Sum counter deltas so an archiver restart inside the window is handled too.
export const ingestBytesInWindow = (samples: SelfMonitorSample[], windowMs = 60 * 60 * 1000): number => {
    if (samples.length < 2) return 0;
    const last = samples[samples.length - 1];
    const firstIndex = samples.findIndex(s => s.time >= last.time - windowMs);
    const baselineIndex = firstIndex < 0 ? samples.length - 1 : Math.max(0, firstIndex - 1);
    let bytes = 0;
    for (let i = baselineIndex + 1; i < samples.length; i++) {
        const current = extraNum(samples[i], TOTAL_KEY);
        const previous = extraNum(samples[i - 1], TOTAL_KEY);
        bytes += current >= previous ? current - previous : current;
    }
    return bytes;
};

export interface IngestCategoryTotal {
    key: string;
    label: string;
    lifetimeBytes: number;
    periodBytes: number;
}

export const buildIngestTotals = (samples: SelfMonitorSample[], periodStart: number): IngestCategoryTotal[] => {
    if (samples.length === 0) return [];
    const keys = categoryKeys(samples);
    const latest = samples[samples.length - 1];
    const firstPeriodIndex = samples.findIndex(s => s.time >= periodStart);
    const baselineIndex = firstPeriodIndex < 0 ? samples.length - 1 : Math.max(0, firstPeriodIndex - 1);

    return keys.map(key => {
        const counterKey = PREFIX + key;
        let periodBytes = 0;
        for (let i = baselineIndex + 1; i < samples.length; i++) {
            const current = extraNum(samples[i], counterKey);
            const previous = extraNum(samples[i - 1], counterKey);
            periodBytes += current >= previous ? current - previous : current;
        }
        return {
            key,
            label: KNOWN_CATEGORIES.find(c => c.key === key)?.label ?? key,
            lifetimeBytes: extraNum(latest, counterKey),
            periodBytes,
        };
    }).filter(total => total.lifetimeBytes > 0 || total.periodBytes > 0);
};

// Chart series, one per category with data in the window, in KB/s - or
// MB/s once the peak reaches 1 MB/s.
export const buildIngestSeries = (samples: SelfMonitorSample[], palette: string[]):
    { series: SelfMonitorSeries[]; unit: string } => {
    const categories = categoryKeys(samples)
        .map((key, slot) => ({ key, slot, data: ingestRates(samples, PREFIX + key) }))
        .filter(c => c.data.some(([, rate]) => rate > 0));
    const peak = Math.max(0, ...categories.flatMap(c => c.data.map(([, rate]) => rate)));
    const unit = peak >= 1024 * 1024 ? 'MB/s' : 'KB/s';
    const divisor = unit === 'MB/s' ? 1024 * 1024 : 1024;
    const series = categories.map(c => ({
        name: KNOWN_CATEGORIES.find(k => k.key === c.key)?.label ?? c.key,
        data: c.data.map(([time, rate]) => [time, rate / divisor] as [number, number]),
        color: palette[c.slot % palette.length],
    }));
    return { series, unit };
};

// Running category totals during the selected period, in megabytes.
export const buildIngestTotalSeries = (samples: SelfMonitorSample[], periodStart: number, palette: string[]):
    { series: SelfMonitorSeries[]; unit: string } => {
    if (samples.length < 2) return { series: [], unit: 'MB' };
    const firstIndex = samples.findIndex(s => s.time >= periodStart);
    const baselineIndex = firstIndex < 0 ? samples.length - 1 : Math.max(0, firstIndex - 1);
    const window = samples.slice(baselineIndex);
    const series = categoryKeys(samples).flatMap((key, slot) => {
        const counterKey = PREFIX + key;
        let total = 0;
        const points: [number, number][] = [];
        window.slice(1).forEach((sample, index) => {
            const previous = extraNum(window[index], counterKey);
            const current = extraNum(sample, counterKey);
            total += current >= previous ? current - previous : current;
            points.push([sample.time, total / (1024 * 1024)]);
        });
        return total > 0 ? [{
            name: KNOWN_CATEGORIES.find(c => c.key === key)?.label ?? key,
            data: points,
            color: palette[slot % palette.length],
        }] : [];
    });
    return { series, unit: 'MB' };
};

// Per-sample byte deltas in megabytes, stacked so each bar shows the total
// data received during that sample interval.
export const buildIngestIntervalSeries = (samples: SelfMonitorSample[], periodStart: number, palette: string[]):
    { series: SelfMonitorSeries[]; unit: string } => {
    if (samples.length < 2) return { series: [], unit: 'MB / interval' };
    const firstIndex = samples.findIndex(s => s.time >= periodStart);
    const baselineIndex = firstIndex < 0 ? samples.length - 1 : Math.max(0, firstIndex - 1);
    const window = samples.slice(baselineIndex);
    const series = categoryKeys(samples).flatMap((key, slot) => {
        const counterKey = PREFIX + key;
        const points = window.slice(1).map((sample, index): [number, number] => {
            const previous = extraNum(window[index], counterKey);
            const current = extraNum(sample, counterKey);
            const delta = current >= previous ? current - previous : current;
            return [sample.time, delta / (1024 * 1024)];
        });
        return points.some(([, value]) => value > 0) ? [{
            name: KNOWN_CATEGORIES.find(c => c.key === key)?.label ?? key,
            data: points,
            color: palette[slot % palette.length],
            chartType: 'bar' as const,
            stack: 'ingest-interval',
        }] : [];
    });
    return { series, unit: 'MB / interval' };
};

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

export const formatBytes = (bytes: number): { value: string; unit: string } => {
    let value = Math.max(0, bytes);
    let unit = 0;
    while (value >= 1024 && unit < UNITS.length - 1) {
        value /= 1024;
        unit++;
    }
    return { value: unit === 0 ? value.toFixed(0) : value.toFixed(1), unit: UNITS[unit] };
};
